import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Utensils,
  Coffee,
  Scissors,
  Dumbbell,
  Pill,
  ShoppingCart,
  Cookie,
  Shirt,
  Flower2,
  Stethoscope,
  Rocket,
  Star,
  MousePointerClick,
  Upload,
  Send,
  PartyPopper,
  GraduationCap,
  Building2,
} from "lucide-react";
import PublicHeader from "@/shared/components/navigation/PublicHeader";
import PublicFooter from "@/shared/components/layout/PublicFooter";
import { Container, Section } from "@/shared/components/layout/Container";
import { DEFAULT_LANDING_CONFIG } from "@/data/configurationRepository";

const BUSINESS_TYPE_ICONS = {
  restaurant: Utensils,
  cafe: Coffee,
  salon: Scissors,
  gym: Dumbbell,
  pharmacy: Pill,
  grocery: ShoppingCart,
  bakery: Cookie,
  boutique: Shirt,
  spa: Flower2,
  clinic: Stethoscope,
  academy: GraduationCap,
  property: Building2,
};

const testimonials = [
  {
    quote:
      "Set up my pizza storefront in under 10 minutes and started receiving live orders the very same day. The insights are incredibly helpful!",
    author: "Rohan M.",
    role: "Owner, Pizza Hub",
  },
  {
    quote:
      "Appointments increased by 40%, waiting times dropped significantly, and business insights now help us identify our most profitable services.",
    author: "Nisha K.",
    role: "Founder, Style Salon",
  },
  {
    quote:
      "This is what my brother's bakery has been missing. Clean, modern, no coding.",
    author: "Aditya S.",
    role: "Early user",
  },
];

const HOW_ICONS = { create: MousePointerClick, menu: Upload, share: Send, orders: PartyPopper, sales: BarChart3 };

export default function LandingPage() {
  const cfg = DEFAULT_LANDING_CONFIG;
  const { hero, faqs, faqSection, howItWorks, businessTypes, finalCta, footer } = cfg;
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader brand={cfg.brand} navigation={cfg.navigation} />

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-40 right-1/4 h-[520px] w-[520px] rounded-full bg-brand/20 blur-[140px]" />
          <div className="absolute top-40 -left-32 h-[520px] w-[520px] rounded-full bg-restaurant/12 blur-[160px]" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-fuchsia-500/10 blur-[120px]" />
        </div>
        <Container className="pt-10 pb-16 sm:pt-14 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              {hero.eyebrow && <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-ink-secondary"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                {hero.eyebrow}
              </motion.div>}
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-[64px] tracking-tighter leading-[1.02]"
                data-testid="hero-title"
              >
                {hero.headlineLine1}
                <br />
                <span className="bg-gradient-to-r from-brand via-fuchsia-400 to-restaurant bg-clip-text text-transparent">
                  {hero.headlineHighlight}{hero.headlineLine3}
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-4 text-lg sm:text-xl text-ink-secondary max-w-2xl leading-relaxed"
              >
                {hero.subtitle}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
              >
                <Link
                  to={hero.ctaPrimaryTo || "/register"}
                  className="btn-primary inline-flex items-center justify-center gap-2 text-base !py-3.5 !px-6"
                  data-testid="hero-cta-register"
                >
                  {hero.ctaPrimary}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={hero.ctaSecondaryTo || "#how-it-works"}
                  className="btn-ghost inline-flex items-center justify-center gap-2 text-base !py-3.5 !px-6"
                  data-testid="hero-cta-how-it-works"
                >
                  {hero.ctaSecondary}
                </a>
              </motion.div>
            </div>

            {/* Hero visual: mock dashboard preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-brand/30 via-fuchsia-500/10 to-restaurant/20 blur-2xl" />
                <div className="relative card-surface p-3 rounded-2xl">
                  <div className="flex items-center gap-2 pb-3 border-b border-line">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-500/70" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                    <span className="ml-3 text-xs text-ink-muted font-mono">
                      Storefront Insights
                    </span>
                  </div>
                  <div className="pt-3 grid grid-cols-2 gap-3">
                    {[
                      { label: "Today’s Sales", value: "₹12,480", accent: "text-brand" },
                      { label: "Orders", value: "38", accent: "text-restaurant" },
                      { label: "Pending Orders", value: "4", accent: "text-amber-300" },
                      { label: "Completed Orders", value: "34", accent: "text-emerald-300" },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="rounded-xl bg-bg-elevated border border-line p-3"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-ink-muted">
                          {s.label}
                        </div>
                        <div
                          className={`mt-1 font-display font-semibold text-lg ${s.accent}`}
                        >
                          {s.value}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-xl bg-bg-elevated border border-line p-3">
                    <div className="text-xs text-ink-muted mb-3">
                      Weekly Revenue
                    </div>
                    <div className="flex items-end gap-1.5 h-16">
                      {[35, 55, 40, 70, 60, 90, 75].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t bg-gradient-to-t from-brand/40 to-brand"
                          style={{ height: `${h}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {[
                      { id: "bestseller", label: "Bestseller", value: "Margherita Pizza" },
                      { id: "monthly-orders", label: "Monthly Orders", value: "1887" },
                    ].map((metric) => (
                      <div
                        key={metric.id}
                        className="rounded-lg bg-bg-elevated border border-line px-3 py-3"
                      >
                        <div className="text-[10px] uppercase tracking-widest text-ink-muted">
                          {metric.label}
                        </div>
                        <div className="mt-1 font-display font-semibold text-lg text-brand">
                          {metric.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <Section id="how-it-works">
        <Container>
          <div className="relative overflow-hidden rounded-3xl border border-line bg-gradient-to-br from-bg-surface to-bg-elevated p-6 sm:p-10 grain">
            <div className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-restaurant/10 blur-3xl" />
            <div className="relative">
              <span className="text-sm uppercase tracking-widest text-brand">
                {howItWorks.eyebrow}
              </span>
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                {howItWorks.steps.map((s, i) => {
                  const StepIcon = HOW_ICONS[s.icon] || MousePointerClick;
                  return (
                    <motion.div
                      key={s.title}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                      className="relative card-surface p-6 card-hover"
                      data-testid={`how-step-${i}`}
                    >
                      <div className="absolute top-4 right-5 font-display text-4xl font-semibold text-white/15 tracking-tighter select-none">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="h-10 w-10 rounded-lg bg-brand-soft border border-brand/30 grid place-items-center">
                        <StepIcon className="h-5 w-5 text-brand" />
                      </div>
                      <h3 className="mt-4 font-display text-lg font-medium">
                        {s.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-ink-secondary leading-relaxed">
                        {s.body}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* BUSINESS TYPES */}
      <Section id="business-types" className="border-t border-line">
        <Container>
          <div>
            <span className="text-sm uppercase tracking-widest text-brand" data-testid="business-types-eyebrow">
              {businessTypes.eyebrow}
            </span>
          </div>
          <div className="mt-10" data-testid="business-types-available">
            <div className="text-xs uppercase tracking-widest text-ink-muted">{businessTypes.availableLabel}</div>
            <div className="mt-3 grid grid-cols-2 gap-3 max-w-2xl">
              {businessTypes.available.filter((item) => item.visible !== false).map((item) => {
                const BusinessIcon = BUSINESS_TYPE_ICONS[item.icon] || Utensils;
                return <div key={item.id} className="relative card-surface border-brand/40 bg-brand-soft p-5 flex items-center gap-4" data-testid={`business-type-${item.id}`}>
                  <div className="h-11 w-11 rounded-xl border border-brand/40 bg-brand-soft grid place-items-center"><BusinessIcon className="h-6 w-6 text-brand" /></div>
                  <div><div className="font-medium text-ink-primary">{item.name}</div><div className="mt-1 text-[11px] uppercase tracking-widest text-success">{businessTypes.availableLabel}</div></div>
                  <span className="absolute top-3 right-3 h-2 w-2 rounded-full bg-success shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
                </div>;
              })}
            </div>
          </div>
          <div className="mt-8" data-testid="business-types-coming">
            <div className="text-xs uppercase tracking-widest text-ink-muted">{businessTypes.comingLabel}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {businessTypes.coming.filter((item) => item.visible !== false).map((item) => {
                const BusinessIcon = BUSINESS_TYPE_ICONS[item.icon] || Building2;
                return <span key={item.id} className="inline-flex items-center gap-2 rounded-full border border-line bg-bg-elevated/60 px-3 py-2 text-sm text-ink-muted" data-testid={`business-type-${item.id}`}><BusinessIcon className="h-4 w-4" />{item.name}</span>;
              })}
            </div>
          </div>
        </Container>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="border-t border-line">
        <Container>
          <span className="text-sm uppercase tracking-widest text-brand">
            Trusted by businesses across the globe
          </span>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="card-surface p-6"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-3.5 w-3.5 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <p className="text-ink-primary leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 pt-4 border-t border-line">
                  <div className="font-medium text-sm">{t.author}</div>
                  <div className="text-xs text-ink-muted">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="border-t border-line">
        <Container>
          <div className="grid lg:grid-cols-3 gap-10">
            <div>
              <span className="text-sm uppercase tracking-widest text-brand">
                {faqSection.eyebrow}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-display font-medium tracking-tight" data-testid="faq-title">
                {faqSection.title}
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-3">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group card-surface p-5"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="font-display text-base font-medium pr-4">
                      {f.q}
                    </span>
                    <span className="h-7 w-7 rounded-full border border-line bg-bg-elevated grid place-items-center text-ink-secondary group-open:rotate-45 transition-transform">
                      <span className="text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <p className="mt-3 text-ink-secondary text-sm leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section className="border-t border-line">
        <Container>
          <div className="relative overflow-hidden rounded-3xl border border-line bg-gradient-to-br from-bg-surface to-bg-elevated p-10 sm:p-16 grain">
            <div className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-restaurant/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <Rocket className="h-8 w-8 text-brand" />
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-display font-medium tracking-tighter" data-testid="final-cta-title">
                {finalCta.title}
              </h2>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to={finalCta.primaryTo}
                  className="btn-primary inline-flex items-center justify-center gap-2 !py-3.5 !px-6"
                  data-testid="cta-register"
                >
                  {finalCta.primaryLabel} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={finalCta.secondaryTo}
                  className="btn-ghost inline-flex items-center justify-center gap-2 !py-3.5 !px-6"
                  data-testid="cta-demo"
                >
                  {finalCta.secondaryLabel}
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <PublicFooter footer={footer} brand={cfg.brand} />
    </div>
  );
}
